import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { cn } from "@/lib/cn";
import { seccionCoincide, vistaActiva, type NombreIcono, type Vista } from "@/lib/navegacion";
import { BarraInferior, type ItemBarra } from "./BarraInferior";
import { Dialog } from "./Dialog";
import { Icono } from "./Icono";
import { Logo2M } from "./Logo2M";
import { MenuUsuario } from "./MenuUsuario";
import { SelectorDesarrollo, type OpcionDesarrollo } from "./SelectorDesarrollo";
import { Row, Stack } from "./Stack";

export interface SeccionNav {
  ruta: string;
  etiqueta: string;
  /** Pantallas que junta esta entrada del menú (ella misma primero); se elige entre ellas arriba del contenido. */
  vistas?: readonly Vista[];
}

export interface GrupoNav {
  id: string;
  etiqueta: string;
  icono: NombreIcono;
  secciones: readonly SeccionNav[];
}

export interface AppShellProps {
  titulo: string;
  nombreUsuario: string;
  grupos: GrupoNav[];
  /** Grupos que van en el menú del nombre (header) en vez de la barra, ej. Configuración. */
  gruposUsuario?: GrupoNav[];
  rutaPerfil: string;
  onSalir: () => void;
  /** Con más de un desarrollo (o permiso para crear), el título se vuelve un `SelectorDesarrollo`. */
  desarrollos?: OpcionDesarrollo[];
  desarrolloActual?: number;
  onCambiarDesarrollo?: (id: number) => void;
  onNuevoDesarrollo?: () => void;
  children: ReactNode;
}

/** Botones de la barra inferior antes de "Más". */
export const MAX_BARRA = 5;

export function grupoActivo(grupo: GrupoNav, pathname: string): boolean {
  return seccionActiva(grupo, pathname) !== undefined;
}

/** La sección del grupo en la que está esa ruta (también desde una ficha de detalle), o undefined. */
export function seccionActiva(grupo: GrupoNav, pathname: string): SeccionNav | undefined {
  return grupo.secciones.find((s) => seccionCoincide(s, pathname));
}

/**
 * Adónde lleva el botón de un grupo en la barra de abajo: a la última pantalla que usaste de ese grupo (o la primera);
 * si ya estás en el grupo, a su primera pantalla (como volver al principio).
 */
export function destinoGrupo(grupo: GrupoNav, pathname: string, recordadas: Record<string, string>): string {
  const primera = grupo.secciones[0]!.ruta;
  if (grupoActivo(grupo, pathname)) return primera;
  const r = recordadas[grupo.id];
  return r && grupo.secciones.some((s) => s.ruta === r || s.vistas?.some((v) => v.ruta === r)) ? r : primera;
}

const esSuelto = (g: GrupoNav) => g.secciones.length === 1 && g.secciones[0]!.etiqueta === g.etiqueta;

function ListaSecciones({ secciones, onNavegar }: { secciones: readonly SeccionNav[]; onNavegar?: () => void }) {
  const { pathname } = useLocation();
  return (
    <ul className="flex flex-col gap-1">
      {secciones.map((s) => {
        const activa = seccionCoincide(s, pathname);
        return (
          <li key={s.ruta}>
            <Link
              to={s.ruta}
              onClick={onNavegar}
              aria-current={activa ? "page" : undefined}
              className={cn("block rounded-full px-3 py-2", activa ? "bg-primario-suave font-medium text-primario" : "text-texto hover:bg-superficie-2")}
            >
              {s.etiqueta}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Selector entre las pantallas que junta una entrada del menú (Mati, 2026-11-03: Presupuesto y Recibidos, Aportes y
 * Previsiones, Fiduciantes y lo que se arma de ellos), arriba del contenido, en la compu y en el celular.
 */
function VistasSeccion({ seccion, pathname }: { seccion: SeccionNav & { vistas: readonly Vista[] }; pathname: string }) {
  const actual = vistaActiva(seccion, pathname)?.ruta;
  return (
    <nav aria-label={seccion.etiqueta} className="mb-5">
      <ul className="inline-flex max-w-full gap-1 overflow-x-auto rounded-full bg-superficie-2 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {seccion.vistas.map((v) => {
          const es = v.ruta === actual;
          return (
            <li key={v.ruta} className="shrink-0">
              <Link
                to={v.ruta}
                aria-current={es ? "page" : undefined}
                className={cn(
                  "block whitespace-nowrap rounded-full px-4 py-1.5 text-chico transition-colors",
                  es ? "bg-superficie font-medium text-texto shadow-sm" : "text-texto-suave hover:text-texto",
                )}
              >
                {v.etiqueta}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Pestañas del grupo en el que estás, sólo en el celular (Mati, 2026-11-03: la hoja que se abría al tocar un grupo tapaba
 * la pantalla). Fila bajo el header que se desliza de costado (no queda fija: se va al scrollear, para no ocupar
 * pantalla); la pestaña activa queda a la vista.
 */
function PestanasGrupo({ grupo, pathname }: { grupo: GrupoNav; pathname: string }) {
  const activa = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    activa.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [pathname]);
  const actual = seccionActiva(grupo, pathname)?.ruta;
  return (
    <nav aria-label={grupo.etiqueta} className="border-b border-texto/10 md:hidden">
      <ul className="flex gap-1 overflow-x-auto px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {grupo.secciones.map((s) => {
          const es = s.ruta === actual;
          return (
            <li key={s.ruta} className="shrink-0">
              <Link
                ref={es ? activa : undefined}
                to={s.ruta}
                aria-current={es ? "page" : undefined}
                className={cn(
                  "block whitespace-nowrap rounded-full px-3 py-1.5 text-chico transition-colors",
                  es ? "bg-primario-suave font-medium text-primario" : "text-texto-suave hover:bg-superficie-2",
                )}
              >
                {s.etiqueta}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Grupo plegable de la barra lateral: el título (con su ícono) abre y cierra sus secciones. */
function GrupoLateral({ grupo, abierto, activo, onAlternar }: { grupo: GrupoNav; abierto: boolean; activo: boolean; onAlternar: () => void }) {
  const id = `menu-${grupo.id}`;
  return (
    <div>
      <button
        type="button"
        aria-expanded={abierto}
        aria-controls={id}
        onClick={onAlternar}
        className={cn("flex w-full items-center gap-2 rounded-full px-3 py-2 text-left hover:bg-superficie-2", activo && !abierto ? "font-medium text-primario" : "text-texto")}
      >
        <Icono nombre={grupo.icono} className="size-5" />
        <span className="min-w-0 flex-1 truncate">{grupo.etiqueta}</span>
        <Icono nombre="flecha" className={cn("size-4 text-texto-suave transition-transform", abierto && "rotate-90")} />
      </button>
      {abierto && (
        <div id={id} className="pl-7 pt-1">
          <ListaSecciones secciones={grupo.secciones} />
        </div>
      )}
    </div>
  );
}

/**
 * Layout de la app logueada: header con menú del usuario; grupos plegables en la barra lateral (compu); en el celular,
 * la barra inferior lleva a cada grupo y las pantallas del grupo actual van como pestañas bajo el header.
 */
export function AppShell({
  titulo, nombreUsuario, grupos, gruposUsuario, rutaPerfil, onSalir, desarrollos, desarrolloActual, onCambiarDesarrollo, onNuevoDesarrollo, children,
}: AppShellProps) {
  const [hoja, setHoja] = useState<string | null>(null); // "mas" con la hoja de los grupos que no entran en la barra
  const { pathname } = useLocation();
  useEffect(() => setHoja(null), [pathname]);

  // Compu: un solo grupo desplegado a la vez; al llegar a una pantalla de otro grupo se despliega ése (en el Inicio, ninguno).
  const idActivo = grupos.find((g) => grupoActivo(g, pathname))?.id ?? null;
  const [desplegado, setDesplegado] = useState<string | null>(idActivo);
  const [activoPrevio, setActivoPrevio] = useState(idActivo);
  if (idActivo !== activoPrevio) {
    setActivoPrevio(idActivo);
    setDesplegado(idActivo);
  }

  const enBarra = grupos.slice(0, MAX_BARRA);
  const resto = grupos.slice(MAX_BARRA);
  const navigate = useNavigate();

  // Celular: la última pantalla usada de cada grupo, para volver a ella desde la barra.
  const [recordadas, setRecordadas] = useState<Record<string, string>>({});
  const grupoActual = grupos.find((g) => g.id === idActivo);
  const seccionDelGrupo = grupoActual && seccionActiva(grupoActual, pathname);
  const rutaActual = seccionDelGrupo ? (vistaActiva(seccionDelGrupo, pathname)?.ruta ?? seccionDelGrupo.ruta) : undefined;
  // La entrada del menú (barra o menú del nombre) en la que estás, si junta varias pantallas.
  const conVistas = [...grupos, ...(gruposUsuario ?? [])].map((g) => seccionActiva(g, pathname)).find((x) => x?.vistas && x.vistas.length > 1);
  if (grupoActual && rutaActual && recordadas[grupoActual.id] !== rutaActual) setRecordadas({ ...recordadas, [grupoActual.id]: rutaActual });

  // Celular: cada grupo va directo a una pantalla y sus hermanas quedan como pestañas arriba; los que no entran, en "Más".
  const itemsBarra: ItemBarra[] = enBarra.map((g) => ({
    clave: g.id,
    etiqueta: g.etiqueta,
    icono: g.icono,
    activo: grupoActivo(g, pathname),
    ruta: destinoGrupo(g, pathname, recordadas),
  }));
  if (resto.length > 0) itemsBarra.push({ clave: "mas", etiqueta: "Más", icono: "mas", activo: resto.some((g) => grupoActivo(g, pathname)) });
  const elegirEnBarra = (item: ItemBarra) => (item.ruta !== undefined ? navigate(item.ruta) : setHoja(item.clave));
  const conSelector = desarrollos !== undefined && desarrolloActual !== undefined && onCambiarDesarrollo && (desarrollos.length > 1 || onNuevoDesarrollo);

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 border-b border-texto/10 bg-fondo/85 backdrop-blur-md">
        <Row justificar="entre" className="mx-auto h-14 max-w-[96rem] px-4">
          <div className="flex min-w-0 items-center gap-3">
            {/* 2M es la desarrolladora; el título o el selector, el desarrollo (Mati, 2026-11-03) */}
            <Link to="/" aria-label="Inicio" className="shrink-0 text-carbon">
              <Logo2M variante="marca" className="w-8" />
            </Link>
            <span aria-hidden="true" className="h-6 w-px shrink-0 bg-texto/15" />
            {conSelector ? (
              <SelectorDesarrollo desarrollos={desarrollos} actual={desarrolloActual} onElegir={onCambiarDesarrollo} onNuevo={onNuevoDesarrollo} />
            ) : (
              <span className="min-w-0 truncate text-[1.25rem] font-light tracking-[-0.02em]">{titulo}</span>
            )}
          </div>
          <MenuUsuario nombre={nombreUsuario} grupos={gruposUsuario} rutaPerfil={rutaPerfil} onSalir={onSalir} />
        </Row>
      </header>
      {grupoActual && grupoActual.secciones.length > 1 && <PestanasGrupo grupo={grupoActual} pathname={pathname} />}
      <div className="mx-auto flex max-w-[96rem] flex-col md:flex-row md:gap-6 md:px-4">
        <nav aria-label="Principal" className="hidden md:block md:w-52 md:shrink-0 md:py-6">
          <Stack espacio={1}>
            {grupos.map((g) =>
              esSuelto(g) ? (
                <NavLink
                  key={g.id}
                  to={g.secciones[0]!.ruta}
                  end={g.secciones[0]!.ruta === "/"}
                  className={({ isActive }) =>
                    cn("flex items-center gap-2 rounded-full px-3 py-2", isActive ? "bg-primario-suave font-medium text-primario" : "text-texto hover:bg-superficie-2")
                  }
                >
                  <Icono nombre={g.icono} className="size-5" />
                  {g.etiqueta}
                </NavLink>
              ) : (
                <GrupoLateral
                  key={g.id}
                  grupo={g}
                  abierto={desplegado === g.id}
                  activo={idActivo === g.id}
                  onAlternar={() => setDesplegado((d) => (d === g.id ? null : g.id))}
                />
              ),
            )}
          </Stack>
        </nav>
        <main className="min-w-0 flex-1 px-4 pb-28 pt-6 md:px-0 md:pb-6">
          {conVistas?.vistas && <VistasSeccion seccion={{ ...conVistas, vistas: conVistas.vistas }} pathname={pathname} />}
          {children}
        </main>
      </div>

      <BarraInferior items={itemsBarra} onElegir={elegirEnBarra} />

      <Dialog abierto={hoja === "mas"} onCambio={(a) => !a && setHoja(null)} titulo="Más">
        <Stack espacio={4}>
          {resto.map((g) => (
            <div key={g.id}>
              <h3 className="px-3 pb-1 text-chico font-semibold uppercase tracking-wide text-texto-suave">{g.etiqueta}</h3>
              <ListaSecciones secciones={g.secciones} onNavegar={() => setHoja(null)} />
            </div>
          ))}
        </Stack>
      </Dialog>
    </div>
  );
}
